import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { pageReady } from "./page-ready";

gsap.registerPlugin(ScrollTrigger);

const DEFAULTS = {
  armStart: "top bottom+=200",
  playStart: "top 75%",
  velocityThreshold: 6000,
};

// Lazy scroll-reveal: builds the timeline only when the section approaches at
// a sane scroll velocity. On a fast fling we never hide the content, avoiding
// the empty-space flash that pre-hide + late trigger produces.
export function createScrollReveal({
  trigger,
  build,
  armStart = DEFAULTS.armStart,
  playStart = DEFAULTS.playStart,
  velocityThreshold = DEFAULTS.velocityThreshold,
}) {
  let state = "idle"; // idle → armed → playing → done
  let tl = null;
  let teardown = null;
  let armTrigger = null;
  let playTrigger = null;

  const finish = () => {
    if (state === "done") return;
    state = "done";
    if (teardown) {
      teardown();
      teardown = null;
    }
    tl = null;
    if (armTrigger) {
      armTrigger.kill();
      armTrigger = null;
    }
    if (playTrigger) {
      playTrigger.kill();
      playTrigger = null;
    }
  };

  const arm = () => {
    if (state !== "idle") return;
    state = "armed";
    const built = build();
    if (built && typeof built === "object" && "tl" in built) {
      tl = built.tl;
      teardown = built.teardown || null;
    } else {
      tl = built;
    }
    if (tl) tl.eventCallback("onComplete", finish);
  };

  const play = () => {
    if (state !== "armed") return;
    state = "playing";
    // Nothing plays behind the loader / page-transition overlay.
    pageReady.ready.then(() => tl?.play());
  };

  // progress(1) suppresses events by default, so finish() must be called
  // manually — otherwise teardown never runs on the snap path.
  const snap = () => {
    if (state !== "armed") return;
    tl.progress(1);
    finish();
  };

  armTrigger = ScrollTrigger.create({
    trigger,
    start: armStart,
    once: true,
    onEnter: (self) => {
      armTrigger = null;
      if (Math.abs(self.getVelocity()) > velocityThreshold) {
        console.log('too fast')
        // Fly past: content stays in its natural visible state, no anim.
        state = "done";
        if (playTrigger) {
          playTrigger.kill();
          playTrigger = null;
        }
        return;
      }
      arm();
    },
  });

  playTrigger = ScrollTrigger.create({
    trigger,
    start: playStart,
    once: true,
    onEnter: (self) => {
      playTrigger = null;
      if (Math.abs(self.getVelocity()) > velocityThreshold) {
        snap();
      } else {
        play();
      }
    },
  });

  return {
    destroy() {
      if (armTrigger) {
        armTrigger.kill();
        armTrigger = null;
      }
      if (playTrigger) {
        playTrigger.kill();
        playTrigger = null;
      }
      if (tl) {
        tl.kill();
        tl = null;
      }
      if (state !== "done" && teardown) {
        teardown();
        teardown = null;
      }
      state = "done";
    },
  };
}
